import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

function ListManager({ isOpen, onClose, onListsUpdate }) {
  const [user, setUser] = useState(null);
  const [customLists, setCustomLists] = useState({});
  const [playedCourses, setPlayedCourses] = useState([]);
  const [wishlistCourses, setWishlistCourses] = useState([]);
  const [deletedDefaultLists, setDeletedDefaultLists] = useState([]);
  const [playedListName, setPlayedListName] = useState('Played');
  const [playedListDescription, setPlayedListDescription] = useState('');
  const [wishlistListName, setWishlistListName] = useState('Bucket List');
  const [wishlistListDescription, setWishlistListDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingList, setEditingList] = useState(null);
  const [editingListName, setEditingListName] = useState('');
  const [editingListDescription, setEditingListDescription] = useState('');
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadUserData(firebaseUser.uid);
      } else {
        setCustomLists({});
        setPlayedCourses([]);
        setWishlistCourses([]);
        setDeletedDefaultLists([]);
        setPlayedListName('Played');
        setPlayedListDescription('');
        setWishlistListName('Bucket List');
        setWishlistListDescription('');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (uid) => {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setCustomLists(data.customLists || {});
        setPlayedCourses(data.playedCourses || []);
        setWishlistCourses(data.wishlistCourses || []);
        setDeletedDefaultLists(data.deletedDefaultLists || []);
        setPlayedListName(data.playedListName || 'Played');
        setPlayedListDescription(data.playedListDescription || '');
        setWishlistListName(data.wishlistListName || 'Bucket List');
        setWishlistListDescription(data.wishlistListDescription || '');
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load lists');
    }
  };

  const createNewList = async () => {
    if (!newListName.trim()) {
      setError('List name is required');
      return;
    }

    try {
      const listId = `list_${Date.now()}`;
      const newList = {
        id: listId,
        name: newListName.trim(),
        description: newListDescription.trim(),
        createdAt: new Date().toISOString(),
        courses: []
      };

      const userRef = doc(db, "users", user.uid);
      const updatedLists = {
        ...customLists,
        [listId]: newList
      };

      await updateDoc(userRef, { customLists: updatedLists });
      setCustomLists(updatedLists);
      setNewListName('');
      setNewListDescription('');
      setSuccess('List created successfully!');
      onListsUpdate && onListsUpdate(updatedLists);
      
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error creating list:', err);
      setError('Failed to create list');
    }
  };

  const startEditingList = (listId) => {
    if (listId === 'played') {
      setEditingList(listId);
      setEditingListName(playedListName);
      setEditingListDescription(playedListDescription);
    } else if (listId === 'wishlist') {
      setEditingList(listId);
      setEditingListName(wishlistListName);
      setEditingListDescription(wishlistListDescription);
    } else {
      const list = customLists[listId];
      setEditingList(listId);
      setEditingListName(list.name);
      setEditingListDescription(list.description || '');
    }
  };

  const cancelEditingList = () => {
    setEditingList(null);
    setEditingListName('');
    setEditingListDescription('');
  };

  const saveEditedList = async (listId) => {
    try {
      const userRef = doc(db, "users", user.uid);
      
      if (listId === 'played') {
        // Save custom name and description for played list
        await updateDoc(userRef, { 
          playedListName: editingListName.trim(),
          playedListDescription: editingListDescription.trim()
        });
        setSuccess('Played list updated successfully!');
      } else if (listId === 'wishlist') {
        // Save custom name and description for wishlist
        await updateDoc(userRef, { 
          wishlistListName: editingListName.trim(),
          wishlistListDescription: editingListDescription.trim()
        });
        setSuccess('Wishlist updated successfully!');
      } else {
        // Handle custom lists
        const updatedLists = {
          ...customLists,
          [listId]: { 
            ...customLists[listId], 
            name: editingListName.trim(),
            description: editingListDescription.trim()
          }
        };

        await updateDoc(userRef, { customLists: updatedLists });
        setCustomLists(updatedLists);
        onListsUpdate && onListsUpdate(updatedLists);
        setSuccess('List updated successfully!');
      }

      setEditingList(null);
      setEditingListName('');
      setEditingListDescription('');
      
      // Reload user data to get updated names
      await loadUserData(user.uid);
      
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error updating list:', err);
      setError('Failed to update list');
    }
  };

  const deleteList = async (listId) => {
    const isDefault = listId === 'played' || listId === 'wishlist';
    const action = isDefault ? 'delete' : 'delete';
    const message = isDefault 
      ? 'Are you sure you want to delete this default list? This will remove it from your profile entirely and cannot be undone.'
      : 'Are you sure you want to delete this list? This will remove all courses from it.';
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      
      if (listId === 'played') {
        // Mark as deleted and clear courses
        const updatedDeletedLists = [...deletedDefaultLists, 'played'];
        await updateDoc(userRef, { 
          playedCourses: [],
          deletedDefaultLists: updatedDeletedLists
        });
        setPlayedCourses([]);
        setDeletedDefaultLists(updatedDeletedLists);
        setSuccess('Played list deleted successfully!');
      } else if (listId === 'wishlist') {
        // Mark as deleted and clear courses
        const updatedDeletedLists = [...deletedDefaultLists, 'wishlist'];
        await updateDoc(userRef, { 
          wishlistCourses: [],
          deletedDefaultLists: updatedDeletedLists
        });
        setWishlistCourses([]);
        setDeletedDefaultLists(updatedDeletedLists);
        setSuccess('Wishlist deleted successfully!');
      } else {
        const updatedLists = { ...customLists };
        delete updatedLists[listId];

        await updateDoc(userRef, { customLists: updatedLists });
        setCustomLists(updatedLists);
        setSuccess('List deleted successfully!');
        onListsUpdate && onListsUpdate(updatedLists);
      }
      
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error deleting list:', err);
      setError('Failed to delete list');
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{ textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Create default lists for display (only if not deleted)
  const defaultLists = [];
  
  if (!deletedDefaultLists.includes('played')) {
    defaultLists.push({
      id: 'played',
      name: playedListName,
      description: playedListDescription,
      courses: playedCourses,
      isDefault: true
    });
  }
  
  if (!deletedDefaultLists.includes('wishlist')) {
    defaultLists.push({
      id: 'wishlist',
      name: wishlistListName,
      description: wishlistListDescription,
      courses: wishlistCourses,
      isDefault: true
    });
  }

  // Sort custom lists by course count (most courses first)
  const sortedCustomLists = Object.values(customLists).sort((a, b) => {
    return b.courses.length - a.courses.length;
  });

  // Combine default and custom lists
  const allLists = [...defaultLists, ...sortedCustomLists];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: '#1D665D', fontSize: '1.5rem' }}>
            Manage My Lists
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fee',
            color: '#c00',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#efe',
            color: '#0a0',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {success}
          </div>
        )}

        {/* Create New List */}
        <div style={{
          background: '#f9f9f9',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1D665D' }}>Create New List</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              List Name
            </label>
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="e.g., Scottsdale Trip"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Description (optional)
            </label>
            <textarea
              value={newListDescription}
              onChange={(e) => setNewListDescription(e.target.value)}
              placeholder="Describe your list..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>
          <button
            onClick={createNewList}
            style={{
              background: '#1D665D',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Create List
          </button>
        </div>

        {/* Existing Lists */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1D665D' }}>My Lists</h3>
          {allLists.length === 0 ? (
            <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>
              No lists yet. Create your first list above!
            </div>
          ) : (
            allLists.map(list => (
              <div key={list.id} style={{
                border: '1px solid #eee',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                background: list.isDefault ? '#f8f9fa' : 'white'
              }}>
                {editingList === list.id ? (
                  <div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <input
                        type="text"
                        value={editingListName}
                        onChange={(e) => setEditingListName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => saveEditedList(list.id)}
                        style={{
                          background: '#1D665D',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditingList}
                        style={{
                          background: '#f0f0f0',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#333' }}>
                          {list.name}
                        </h4>
                        <span style={{ color: '#888', fontSize: '12px' }}>
                          {list.courses.length} course{list.courses.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => startEditingList(list.id)}
                          style={{
                            background: '#1D665D',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteList(list.id)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ListManager; 